<template>
	<div
		class="dashboard-filter-panel"
	>
		<!-- Header row - always visible -->
		<div class="filter-header">
			<div class="date-range-header">
				<!-- Dashboard Selector - first item -->
				<div class="filter-field-col dashboard-col">
					<Dropdown
						v-model="selectedDashboard"
						label="Dashboard"
						:items="dashboardOptions"
						outlined
						dense
						hide-details
						@change="onDashboardChange"
					/>
				</div>

				<!-- Brand Filter -->
				<div
					v-if="showBrandFilter"
					class="filter-field-col brand-col"
				>
					<Dropdown
						v-model="editableFilters.brandId"
						:label="'Brand' | useLabels"
						:items="brandFilterItems"
						outlined
						dense
						hide-details
						@change="updateFilter('brandId', $event)"
					/>
				</div>

				<!-- Date Range Selector -->
				<div class="filter-field-col date-range-col">
					<DateRangeSelector
						:start-date="editableFilters.startDate"
						:end-date="editableFilters.endDate"
						:period="editableFilters.periodValue"
						button-class="date-range-btn"
						@apply="onDateRangeApply"
					/>
				</div>
				<div
					v-if="showCookieFilter"
					class="filter-field-col banner-col"
				>
					<Dropdown
						v-model="editableFilters.widgetValue"
						label="Consent Banner"
						:items="widgetOptions"
						outlined
						dense
						hide-details
						custom-sort
						@change="updateFilter('widgetValue', $event)"
					/>
				</div>
				<div
					v-if="showCookieFilter"
					class="filter-field-col domain-col"
				>
					<Dropdown
						v-model="editableFilters.domainValue"
						label="Domain"
						:items="domainItems"
						outlined
						dense
						hide-details
						@change="updateFilter('domainValue', $event)"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import DateRangeSelector from '../../../../../shared/components/date-range-selector.vue'
import Dropdown from '../../../../../shared/components/dropdown.vue'
import { navDrawerMixin } from './mixins/navDrawerMixin.js'
import { filterStateMixin } from './mixins/filterStateMixin.js'
import './dashboard-filter-accordion.css'

export default {
	name: 'dashboard-filter-accordion',
	components: {
		DateRangeSelector,
		Dropdown
	},
	mixins: [navDrawerMixin, filterStateMixin],
	props: {
		currentDashboardId: {
			type: Number,
			default: null
		}
	},
	data () {
		return {
			updatingFromPeriod: false, // Flag to prevent custom switching when updating from period
			selectedDashboard: null // Selected dashboard ID
		}
	},
	computed: {
		/**
		 * Map dashboards from store to select items with icons and Title Case names
		 */
		...mapGetters('dashboards', ['dashboards']),
		dashboardOptions () {
			return (this.dashboards || []).map(({ id, name }) => ({
				value: id,
				text: name,
				icon: this.getDashboardIcon(id)
			}))
		}
	},
	watch: {
		/**
		 * Watch for period value changes to automatically update dates
		 */
		'editableFilters.periodValue': {
			handler (newPeriod, oldPeriod) {
				// Only update dates if the period actually changed and it's not Custom
				if (newPeriod && newPeriod !== oldPeriod && newPeriod !== this.Periods?.Custom && !this.updatingFromPeriod) {
					this.updateDatesForPeriod(newPeriod)
				}
			},
			immediate: false
		},

		/**
		 * Watch for current dashboard ID changes from parent
		 */
		currentDashboardId: {
			handler (newDashboardId) {
				if (newDashboardId !== null && newDashboardId !== this.selectedDashboard) {
					// Ensure the dashboard ID is an integer
					this.selectedDashboard = parseInt(newDashboardId, 10)
				}
			},
			immediate: true
		},

		/**
		 * Watch for navigation drawer state changes
		 */
		isNavDrawerCollapsed: {
			handler () {
				this.updateFilterPanelWidth()
			},
			immediate: true
		}
	},
	mounted () {
		// Initialize dates based on current period value
		if (this.editableFilters?.periodValue && this.editableFilters.periodValue !== this.Periods?.Custom) {
			this.updateDatesForPeriod(this.editableFilters.periodValue)
		}

		// Set up initial filter panel width
		this.updateFilterPanelWidth()
	},
	methods: {
		/**
		 * Calculate start and end dates based on the selected period
		 * @param {string} period - The selected period value
		 */
		updateDatesForPeriod (period) {
			const today = new Date()
			let endDate = new Date(today)
			let startDate = new Date(today)

			// Calculate start date based on period
			switch (period) {
				case this.Periods?.Last7Days:
					startDate.setDate(today.getDate() - 7)
					break
				case this.Periods?.Last30Days:
					startDate.setDate(today.getDate() - 30)
					break
				case this.Periods?.Last90Days:
					startDate.setDate(today.getDate() - 90)
					break
				case this.Periods?.Last6Months:
					startDate.setMonth(today.getMonth() - 6)
					break
				case this.Periods?.Last12Months:
					startDate.setFullYear(today.getFullYear() - 1)
					break
				case this.Periods?.ThisMonth:
					startDate.setDate(1) // First day of current month
					break
				case this.Periods?.LastMonth:
					startDate.setMonth(today.getMonth() - 1, 1) // First day of last month
					endDate.setDate(0) // Last day of last month
					break
				case this.Periods?.ThisYear:
					startDate = new Date(today.getFullYear(), 0, 1) // January 1st of current year
					break
				case this.Periods?.LastYear:
					startDate = new Date(today.getFullYear() - 1, 0, 1) // January 1st of last year
					endDate = new Date(today.getFullYear() - 1, 11, 31) // December 31st of last year
					break
				default:
					// For any unknown period, keep current dates
					return
			}

			// Update the date fields without triggering the custom period switch
			this.updatingFromPeriod = true
			this.editableFilters.startDate = startDate.toISOString().split('T')[0]
			this.editableFilters.endDate = endDate.toISOString().split('T')[0]

			// Update filters through mixin
			this.updateFilter('startDate', this.editableFilters.startDate)
			this.updateFilter('endDate', this.editableFilters.endDate)

			this.$nextTick(() => {
				this.updatingFromPeriod = false
			})
		},

		/**
		 * Update the filter panel width based on nav drawer state
		 */
		updateFilterPanelWidth () {
			const navDrawerWidth = this.getNavDrawerWidth()
			const filterPanelWidth = `calc(100vw - ${navDrawerWidth}px)`

			// Set CSS custom property on the filter panel element
			this.$nextTick(() => {
				const filterPanel = this.$el
				if (filterPanel) {
					filterPanel.style.setProperty('--filter-panel-width', filterPanelWidth)
				}
			})
		},
		/**
		 * Updates a specific filter value
		 * @param {string} key - The filter key to update
		 * @param {*} value - The new value for the filter
		 */
		updateFilter (key, value) {
			// Call the mixin method to update the filter
			this.$options.mixins.find(mixin => mixin.methods?.updateFilter)?.methods.updateFilter.call(this, key, value)
		},

		/**
		 * Handle dashboard selection change
		 * @param {number|string} dashboardId - The ID of the selected dashboard
		 */
		onDashboardChange (dashboardId) {
			// Ensure dashboard ID is an integer
			const numericDashboardId = parseInt(dashboardId, 10)

			// Find the dashboard by ID
			const dashboard = this.dashboards.find(d => d.id === numericDashboardId)

			if (dashboard) {
				this.selectedDashboard = numericDashboardId

				// Always emit the event to ensure parent component processes the change
				this.$emit('dashboard-change', numericDashboardId)
			} else {
				console.error('Dashboard not found with ID:', numericDashboardId)
			}
		},

		/**
		 * Returns the appropriate Material Design icon for each dashboard type
		 * @param {number} dashboardId - The ID of the dashboard
		 * @returns {string} The icon name for Vuetify v-icon
		 */
		getDashboardIcon (dashboardId) {
			switch (dashboardId) {
				case 0: // Cookie Module (legacy)
					return 'mdi-shield-check-outline'
				case 1: // Data Processing
					return 'mdi-swap-horizontal'
				case 3: // Consent Management
					return 'mdi-account-check-outline'
				case 4: // Associated Cookie Submissions
					return 'mdi-file-document-multiple'
				case 5: // Cookie Fragments
					return 'mdi-puzzle'
				default: // Other custom dashboards
					return 'mdi-chart-line'
			}
		},

		/**
		 * Handle date range selector apply
		 * @param {Object} dateRange - The selected date range
		 */
		onDateRangeApply (dateRange) {
			const { period, startDate, endDate } = dateRange

			// Update period first
			this.updateFilter('periodValue', period)

			// Update dates
			this.updateFilter('startDate', startDate)
			this.updateFilter('endDate', endDate)
		}
	}
}
</script>
